/**
 * Uploads wherever they start (section 5): an item's Video section, the header's Upload button,
 * Files. One place keeps this browser's uploads and the brand's media list, so every screen shows
 * the same state for a video: Uploading 42% → Waiting for the encoder → Encoding → Ready (or Failed).
 * While anything is unfinished the list is fetched again every few seconds; an upload keeps going
 * while the editor moves between screens (the tab must stay open).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { media, type MediaAsset } from "@/api/admin";
import type { AdminClient } from "@/api/client";
import { uploadVideo } from "@/lib/video-upload";
import { videoState, type UploadState } from "./states";

export interface LocalUpload {
  key: string;
  title: string;
  assetId?: string;
  progress: number;
  error?: string;
}

export interface UploadCenter {
  assets: MediaAsset[] | null;
  uploads: LocalUpload[];
  refresh: () => Promise<void>;
  /** Starts an upload; resolves with the new video's id as soon as it exists (before the bytes are sent). */
  startVideo: (file: File, title: string, ladder: string) => Promise<string>;
  cancel: (key: string) => void;
  dismiss: (key: string) => void;
  /** Where a video stands, from this browser's upload and the backend's list. */
  stateOf: (assetId: string) => UploadState | null;
  asset: (assetId: string) => MediaAsset | undefined;
}

const Context = createContext<UploadCenter | null>(null);

export function useUploads(): UploadCenter {
  const center = useContext(Context);
  if (!center) throw new Error("useUploads outside UploadProvider");
  return center;
}

const POLL_MS = 5000;

export function UploadProvider({ client, children }: { client: AdminClient; children: ReactNode }) {
  const [assets, setAssets] = useState<MediaAsset[] | null>(null);
  const [uploads, setUploads] = useState<LocalUpload[]>([]);
  const aborts = useRef(new Map<string, AbortController>());

  const refresh = useCallback(async () => {
    try { setAssets((await media.list(client)).assets.filter((a) => a.status !== "purged")); } catch { setAssets((a) => a ?? []); }
  }, [client]);
  useEffect(() => { void refresh(); }, [refresh]);

  const patch = (key: string, change: Partial<LocalUpload>) => setUploads((list) => list.map((u) => (u.key === key ? { ...u, ...change } : u)));

  const startVideo = useCallback((file: File, title: string, ladder: string) => new Promise<string>((resolve, reject) => {
    const key = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const abort = new AbortController();
    aborts.current.set(key, abort);
    setUploads((list) => [...list, { key, title, progress: 0 }]);
    let started = false;
    uploadVideo(client, file, title, ladder, (progress) => patch(key, { progress }), abort.signal, (assetId) => {
      started = true;
      patch(key, { assetId });
      void refresh();
      resolve(assetId);
    })
      .then(() => { patch(key, { progress: 1 }); return refresh(); })
      .catch((e: unknown) => {
        const message = abort.signal.aborted ? "The upload was cancelled." : e instanceof Error ? e.message : String(e);
        patch(key, { error: message });
        void refresh();
        if (!started) reject(e);
      })
      .finally(() => aborts.current.delete(key));
  }), [client, refresh]);

  const stateOf = useCallback((assetId: string) => {
    const asset = assets?.find((a) => a.id === assetId);
    const local = uploads.find((u) => u.assetId === assetId);
    if (!asset && !local) return assets ? null : videoState(null);
    return videoState(asset ?? null, local ? { progress: local.progress, error: local.error } : undefined);
  }, [assets, uploads]);

  // Look again while anything is on its way: a local upload, or a video the encoder has not finished.
  const busy = uploads.some((u) => !u.error && u.progress < 1) || (assets ?? []).some((a) => a.kind === "video" && a.status === "uploading");
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(t);
  }, [busy, refresh]);

  // Leaving with an upload under way would stop it.
  useEffect(() => {
    const sending = uploads.some((u) => !u.error && u.progress < 1);
    if (!sending) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploads]);

  const value = useMemo<UploadCenter>(() => ({
    assets, uploads, refresh, startVideo, stateOf,
    asset: (id) => assets?.find((a) => a.id === id),
    cancel: (key) => aborts.current.get(key)?.abort(),
    dismiss: (key) => setUploads((list) => list.filter((u) => u.key !== key)),
  }), [assets, uploads, refresh, startVideo, stateOf]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}
